import React from "react";
import PublicPageLayout from "./PublicPageLayout";

function FeatureIcon({ type, size = 22 }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  if (type === "work") {
    return (
      <svg {...common}>
        <rect x="5" y="3" width="14" height="18" rx="2" />
        <path d="M9 3.5V2h6v1.5" />
        <path d="M8 8h8M8 12h8M8 16h5" />
      </svg>
    );
  }

  if (type === "fleet") {
    return (
      <svg {...common}>
        <path d="M3 6h11v10H3z" />
        <path d="M14 10h4l3 3v3h-7z" />
        <circle cx="7" cy="18" r="2" />
        <circle cx="18" cy="18" r="2" />
      </svg>
    );
  }

  if (type === "report") {
    return (
      <svg {...common}>
        <path d="M4 20V10" />
        <path d="M10 20V4" />
        <path d="M16 20v-7" />
        <path d="M22 20V7" />
      </svg>
    );
  }

  return null;
}

function ImagePlaceholder({ label }) {
  return (
    <div className="product-image-placeholder">
      <div className="placeholder-grid" />

      <div className="placeholder-copy">
        <span>IMAGE</span>
        <strong>{label}</strong>
        <small>圖片之後放這裡</small>
      </div>
    </div>
  );
}

export default function Products() {
  return (
    <PublicPageLayout
      title={
        <>
          歲序更迭，<span className="product-title-accent">運行不輟。</span>
        </>
      }
      intro="TALLARGE 整合工作、人車管理、報表紀錄，將碎片化的資訊整合為完整的營運脈絡，讓日常管理清晰有序。"
    >
      <style>{styles}</style>

      <section className="product-showcase">
        {/* =========================
            左側功能文字
           ========================= */}

        <div className="feature-block feature-work">
          <div className="feature-heading">
            <span className="feature-icon orange">
              <FeatureIcon type="work" />
            </span>

            <h2>工作管理</h2>
          </div>

          <p>
            起訖資訊、承運人車與執行狀態一覽可查，
            減少溝通往返，提升調度效率。
          </p>
        </div>

        <div className="feature-block feature-report">
          <div className="feature-heading">
            <span className="feature-icon blue">
              <FeatureIcon type="report" />
            </span>

            <h2>報表紀錄</h2>
          </div>

          <p>
            每日查閱運輸明細、費用支出、人員出勤與車輛保養紀錄，
            支援報表匯出，讓資料核對更便捷、營運追溯更完整。
          </p>
        </div>

        {/* =========================
            中央產品展示
           ========================= */}

        <div className="product-devices">
          <div className="device-shadow" />

          {/* 左手機 */}
          <div className="device device-job">
            <div className="phone-shell">
              <div className="phone-speaker" />

              <div className="phone-screen">
                <ImagePlaceholder label="工作管理畫面" />
              </div>
            </div>
          </div>

          {/* 中間手機 */}
          <div className="device device-route">
            <div className="phone-shell">
              <div className="phone-speaker" />

              <div className="phone-screen">
                <ImagePlaceholder label="導航 / 工作執行畫面" />
              </div>
            </div>
          </div>

          {/* 右側地圖 */}
          <div className="device device-map">
            <div className="map-shell">
              <ImagePlaceholder label="即時人車地圖" />
            </div>
          </div>
        </div>

        {/* =========================
            右側功能
           ========================= */}

        <div className="feature-block feature-fleet">
          <div className="feature-heading">
            <span className="feature-icon green">
              <FeatureIcon type="fleet" />
            </span>

            <h2>人車管理</h2>
          </div>

          <p>
            迅速掌握人員定位、上班狀態與車輛位置，
            清楚查閱最後停放地點及曳引車與拖車配置，
            讓調派安排更精準。
          </p>
        </div>
      </section>

      {/* =========================
          行動版 / 平板補充區
         ========================= */}

      <section className="mobile-feature-list">
        <article className="mobile-feature-row">
          <span className="feature-icon orange">
            <FeatureIcon type="work" />
          </span>

          <div>
            <h3>工作管理</h3>
            <p>
              起訖資訊、承運人車與執行狀態一覽可查，
              減少溝通往返，提升調度效率。
            </p>
          </div>
        </article>

        <article className="mobile-feature-row">
          <span className="feature-icon green">
            <FeatureIcon type="fleet" />
          </span>

          <div>
            <h3>人車管理</h3>
            <p>
              掌握人員定位、上班狀態、車輛位置與車組配置，
              讓調派資訊更加清楚。
            </p>
          </div>
        </article>

        <article className="mobile-feature-row">
          <span className="feature-icon blue">
            <FeatureIcon type="report" />
          </span>

          <div>
            <h3>報表紀錄</h3>
            <p>
              查閱運輸、費用、出勤及保養紀錄，
              支援報表匯出與後續營運追溯。
            </p>
          </div>
        </article>
      </section>
    </PublicPageLayout>
  );
}

const styles = `
/* =========================================================
   Products Page
   ========================================================= */

.product-title-accent {
  color: inherit;
}


/* =========================================================
   Hero
   重點：不要佔掉半個螢幕
   ========================================================= */

.public-hero {
  border-bottom: 0;

  background:
    radial-gradient(
      circle at 84% 26%,
      rgba(255, 170, 95, 0.08),
      transparent 28%
    ),
    #fff;
}

.public-hero-inner {
  width: min(1180px, calc(100% - 48px));

  margin: 0 auto;

  padding:
    clamp(36px, 5.2vh, 54px)
    0
    clamp(14px, 2vh, 22px);

  text-align: center;
}

.public-hero h1 {
  max-width: 900px;

  margin: 0 auto;

  color: #20242a;

  font-family:
    -apple-system,
    BlinkMacSystemFont,
    "SF Pro Display",
    "PingFang TC",
    "Noto Sans TC",
    "Microsoft JhengHei",
    sans-serif;

  font-size:
    clamp(
      38px,
      4.1vw,
      58px
    );

  font-weight: 650;

  line-height: 1.08;

  letter-spacing: -0.055em;
}

.public-hero p {
  max-width: 680px;

  margin:
    clamp(14px, 2vh, 20px)
    auto
    0;

  color: #626b75;

  font-size:
    clamp(
      14px,
      1.2vw,
      17px
    );

  font-weight: 400;

  line-height: 1.72;

  letter-spacing: -0.015em;
}


/* =========================================================
   Public content
   ========================================================= */

.public-content {
  background:
    linear-gradient(
      to bottom,
      #fff 0%,
      #fff 75%,
      #fafbfc 100%
    );
}

.public-content-inner {
  width:
    min(
      1320px,
      calc(100% - 48px)
    );

  margin: 0 auto;

  padding:
    0
    0
    clamp(55px, 7vh, 90px);
}


/* =========================================================
   Main showcase
   ========================================================= */

.product-showcase {
  position: relative;

  width: 100%;

  min-height:
    clamp(
      475px,
      56vh,
      555px
    );

  margin: 0 auto;

  overflow: hidden;
}


/* =========================================================
   Feature blocks
   ========================================================= */

.feature-block {
  position: absolute;

  z-index: 10;

  width: 270px;
}

.feature-heading {
  display: flex;

  align-items: center;

  gap: 13px;
}

.feature-heading h2 {
  margin: 0;

  color: #20242a;

  font-family:
    -apple-system,
    BlinkMacSystemFont,
    "SF Pro Display",
    "PingFang TC",
    "Noto Sans TC",
    "Microsoft JhengHei",
    sans-serif;

  font-size: 23px;

  font-weight: 650;

  line-height: 1.2;

  letter-spacing: -0.035em;
}

.feature-block p {
  margin: 13px 0 0;

  color: #65707b;

  font-size: 14px;

  font-weight: 400;

  line-height: 1.75;

  letter-spacing: -0.012em;
}


/* =========================================================
   Icons
   ========================================================= */

.feature-icon {
  display: grid;

  place-items: center;

  flex: 0 0 auto;

  width: 46px;
  height: 46px;

  border-radius: 50%;

  color: #fff;

  box-shadow:
    0 9px 22px
    rgba(
      30,
      40,
      52,
      0.10
    );
}

.feature-icon.orange {
  background:
    linear-gradient(
      145deg,
      #ff9b3d,
      #ff7517
    );
}

.feature-icon.green {
  background:
    linear-gradient(
      145deg,
      #38cb7d,
      #12ad5c
    );
}

.feature-icon.blue {
  background:
    linear-gradient(
      145deg,
      #5599ee,
      #3475cf
    );
}


/* =========================================================
   Desktop feature positions
   ========================================================= */

.feature-work {
  top: 72px;
  left: 0;
}

.feature-report {
  left: 0;

  bottom: 54px;

  width: 290px;
}

.feature-fleet {
  right: 0;

  bottom: 74px;

  width: 285px;
}


/* =========================================================
   Device composition
   ========================================================= */

.product-devices {
  position: absolute;

  top: 0;

  left: 50%;

  width: 720px;

  height: 515px;

  transform: translateX(-50%);
}

.device {
  position: absolute;
}

.device-shadow {
  position: absolute;

  right: 60px;

  bottom: 28px;

  left: 60px;

  height: 65px;

  border-radius: 50%;

  background:
    rgba(
      29,
      39,
      50,
      0.10
    );

  filter: blur(30px);
}


/* =========================================================
   Phone 1
   ========================================================= */

.device-job {
  z-index: 4;

  top: 52px;

  left: 55px;

  width: 220px;

  height: 390px;

  transform:
    rotate(-5.5deg);
}


/* =========================================================
   Phone 2
   ========================================================= */

.device-route {
  z-index: 6;

  top: 48px;

  left: 268px;

  width: 214px;

  height: 400px;

  transform:
    rotate(3.5deg);
}


/* =========================================================
   Phone shell
   ========================================================= */

.phone-shell {
  position: relative;

  width: 100%;
  height: 100%;

  padding: 5px;

  overflow: hidden;

  border:
    3px solid
    #1d2024;

  border-radius: 29px;

  background: #1d2024;

  box-shadow:
    0 20px 50px
    rgba(
      28,
      38,
      48,
      0.15
    ),
    0 6px 15px
    rgba(
      28,
      38,
      48,
      0.07
    );
}

.phone-screen {
  width: 100%;
  height: 100%;

  overflow: hidden;

  border-radius: 23px;

  background: #fff;
}

.phone-speaker {
  position: absolute;

  z-index: 5;

  top: 8px;

  left: 50%;

  width: 61px;

  height: 7px;

  border-radius: 30px;

  background: #111;

  transform:
    translateX(-50%);
}


/* =========================================================
   Map
   ========================================================= */

.device-map {
  z-index: 2;

  top: 86px;

  right: 4px;

  width: 305px;

  height: 205px;

  transform:
    rotate(-4deg);
}

.map-shell {
  width: 100%;
  height: 100%;

  padding: 4px;

  overflow: hidden;

  border:
    1px solid
    rgba(
      225,
      229,
      234,
      0.9
    );

  border-radius: 10px;

  background: #fff;

  box-shadow:
    0 18px 42px
    rgba(
      31,
      42,
      53,
      0.11
    );
}


/* =========================================================
   Image placeholder
   ========================================================= */

.product-image-placeholder {
  position: relative;

  width: 100%;
  height: 100%;

  overflow: hidden;

  display: flex;

  align-items: center;

  justify-content: center;

  background:
    linear-gradient(
      145deg,
      #fafbfc,
      #f1f4f6
    );
}

.placeholder-grid {
  position: absolute;

  inset: 0;

  opacity: 0.42;

  background-image:
    linear-gradient(
      rgba(
        80,
        95,
        110,
        0.07
      )
      1px,
      transparent 1px
    ),
    linear-gradient(
      90deg,
      rgba(
        80,
        95,
        110,
        0.07
      )
      1px,
      transparent 1px
    );

  background-size:
    23px 23px;
}

.placeholder-copy {
  position: relative;

  z-index: 2;

  display: flex;

  flex-direction: column;

  align-items: center;

  gap: 5px;

  padding: 14px;

  text-align: center;
}

.placeholder-copy > span {
  color: #ea7b34;

  font-size: 8px;

  font-weight: 700;

  letter-spacing: 0.15em;
}

.placeholder-copy strong {
  color: #69747e;

  font-size: 12px;

  font-weight: 600;
}

.placeholder-copy small {
  color: #a3abb3;

  font-size: 9px;
}


/* =========================================================
   Mobile feature rows
   ========================================================= */

.mobile-feature-list {
  display: none;
}


/* =========================================================
   Large desktop
   稍微限制超寬螢幕，不讓元素散太開
   ========================================================= */

@media (min-width: 1500px) {

  .public-content-inner {
    width:
      min(
        1280px,
        calc(100% - 64px)
      );
  }

}


/* =========================================================
   Short desktop
   你的截圖最需要這個
   ========================================================= */

@media
  (min-width: 1001px)
  and
  (max-height: 820px) {

  .public-hero-inner {
    padding:
      28px
      0
      10px;
  }

  .public-hero h1 {
    font-size:
      clamp(
        34px,
        3.8vw,
        50px
      );
  }

  .public-hero p {
    max-width: 620px;

    margin-top: 12px;

    font-size: 14px;

    line-height: 1.6;
  }

  .product-showcase {
    min-height: 455px;
  }

  .product-devices {
    width: 650px;

    height: 440px;
  }

  .device-job {
    top: 36px;

    left: 49px;

    width: 195px;

    height: 345px;
  }

  .device-route {
    top: 34px;

    left: 238px;

    width: 190px;

    height: 354px;
  }

  .device-map {
    top: 67px;

    width: 274px;

    height: 185px;
  }

  .feature-work {
    top: 55px;
  }

  .feature-report {
    bottom: 46px;
  }

  .feature-fleet {
    bottom: 62px;
  }

}


/* =========================================================
   Tablet
   ========================================================= */

@media (max-width: 1100px) {

  .public-content-inner {
    width:
      min(
        100% - 36px,
        960px
      );
  }

  .product-showcase {
    min-height: 670px;
  }

  .product-devices {
    position: relative;

    top: auto;

    left: auto;

    width: min(700px, 100%);

    height: 500px;

    margin: 0 auto;

    transform: none;
  }

  .feature-work,
  .feature-report,
  .feature-fleet {
    display: none;
  }

  .mobile-feature-list {
    display: grid;

    grid-template-columns:
      repeat(
        3,
        minmax(
          0,
          1fr
        )
      );

    gap: 12px;

    width: 100%;

    margin: 8px auto 0;
  }

  .mobile-feature-row {
    display: flex;

    flex-direction: column;

    align-items: flex-start;

    gap: 13px;

    padding: 18px;

    border:
      1px solid
      #edf0f3;

    border-radius: 17px;

    background:
      rgba(
        255,
        255,
        255,
        0.96
      );
  }

  .mobile-feature-row h3 {
    margin: 0;

    color: #20242a;

    font-size: 17px;

    font-weight: 650;

    letter-spacing:
      -0.025em;
  }

  .mobile-feature-row p {
    margin: 6px 0 0;

    color: #707a84;

    font-size: 12px;

    line-height: 1.65;
  }

  .mobile-feature-row
  .feature-icon {
    width: 42px;

    height: 42px;
  }

}


/* =========================================================
   Smaller tablet
   ========================================================= */

@media (max-width: 760px) {

  .public-hero-inner {
    padding:
      42px
      0
      18px;
  }

  .public-hero h1 {
    font-size:
      clamp(
        38px,
        8vw,
        52px
      );
  }

  .public-hero p {
    max-width: 520px;

    font-size: 14px;
  }

  .product-showcase {
    min-height: auto;
  }

  .product-devices {
    height: 450px;
  }

  .mobile-feature-list {
    grid-template-columns:
      1fr;
  }

  .mobile-feature-row {
    display: grid;

    grid-template-columns:
      auto
      1fr;

    align-items: center;
  }

}


/* =========================================================
   Mobile
   ========================================================= */

@media (max-width: 620px) {

  .public-hero-inner {
    width:
      calc(
        100% - 34px
      );

    padding:
      34px
      0
      15px;
  }

  .public-hero h1 {
    max-width: 380px;

    font-size:
      clamp(
        34px,
        10vw,
        43px
      );

    font-weight: 650;

    line-height: 1.08;

    letter-spacing:
      -0.05em;
  }

  .public-hero p {
    max-width: 360px;

    margin-top: 14px;

    font-size: 13px;

    line-height: 1.65;
  }

  .public-content-inner {
    width:
      calc(
        100% - 28px
      );

    padding-bottom: 55px;
  }

  .product-devices {
    width: 100%;

    height: 370px;

    margin-top: 0;
  }

  .device-job {
    top: 45px;

    left: 2%;

    width: 43%;

    height: 285px;

    transform:
      rotate(-4.5deg);
  }

  .device-route {
    top: 51px;

    left: 34%;

    width: 42%;

    height: 295px;

    transform:
      rotate(3.5deg);
  }

  .device-map {
    top: 77px;

    right: 0;

    width: 45%;

    height: 145px;

    transform:
      rotate(-4deg);
  }

  .phone-shell {
    padding: 4px;

    border-width: 2px;

    border-radius: 22px;
  }

  .phone-screen {
    border-radius: 18px;
  }

  .phone-speaker {
    top: 6px;

    width: 46px;

    height: 5px;
  }

  .map-shell {
    padding: 3px;

    border-radius: 7px;
  }

  .placeholder-copy {
    padding: 5px;
  }

  .placeholder-copy > span {
    font-size: 6px;
  }

  .placeholder-copy strong {
    font-size: 8px;
  }

  .placeholder-copy small {
    display: none;
  }

  .mobile-feature-list {
    gap: 9px;

    margin-top: 0;
  }

  .mobile-feature-row {
    grid-template-columns:
      40px
      1fr;

    gap: 12px;

    padding:
      14px;

    border-radius: 14px;
  }

  .mobile-feature-row
  .feature-icon {
    width: 40px;

    height: 40px;
  }

  .mobile-feature-row h3 {
    font-size: 16px;
  }

  .mobile-feature-row p {
    font-size: 12px;

    line-height: 1.55;
  }

}


/* =========================================================
   Small phones
   ========================================================= */

@media (max-width: 390px) {

  .public-hero h1 {
    font-size: 34px;
  }

  .product-devices {
    height: 345px;
  }

  .device-job {
    height: 265px;
  }

  .device-route {
    height: 275px;
  }

  .device-map {
    height: 132px;
  }

}
`;